/**
 * Cortex API Client
 * 
 * Client for interacting with the Cortex API.
 * Handles SSE streaming for real-time cortex updates.
 * 
 * Based on cortex-api/src/api/decisionStream.ts
 */

import { getJWTToken } from '../auth/token-storage.js';
import { log } from '../logging/index.js';
import { handleFetchError, handleHTTPError } from '../error-handler/index.js';
import { createParser, type EventSourceMessage } from 'eventsource-parser';

// Cortex API runs on port 8787 locally (see cortex-api/wrangler.toml)
const CORTEX_API_BASE_URL = 
  import.meta.env.DEV 
    ? 'http://localhost:8787' 
    : 'https://pivot-is-cortex-api.dont-d23.workers.dev';

/**
 * Cortex Request
 * 
 * Request to the Cortex API for conversation, decision-making, file analysis, etc.
 * Not limited to decisions - covers all interaction types.
 */
export interface CortexRequest {
  userInput: string;
  goal?: string;
  context?: Record<string, any>;
  orgId?: string;
}

export interface ConversationalEvent {
  cortex: string;
  message: string;
  tone?: string;
  intent?: string;
  timestamp: number;
}

export interface CortexStartedEvent {
  cortex: string;
  timestamp: number;
}

export interface CortexCompletedEvent {
  cortex: string;
  confidence?: number;
  durationMs?: number;
  goal?: string;
  options?: any[];
  summary?: string;
  evidence?: any[];
  projections?: any[];
  bestOption?: string;
  evaluations?: any[];
  decisionGraph?: any;
  recommendation?: string;
  rationale?: string;
  timestamp: number;
}

export interface CortexWaitingEvent {
  cortex: string;
  waitingFor: string[];
  timestamp: number;
}

export interface CortexBlockedEvent {
  cortex: string;
  reason: "activation_criteria" | "tier_restriction" | "disabled_dependency" | "circular_dependency";
  details?: string;
  timestamp: number;
}

export interface CortexSkippedEvent {
  cortex: string;
  reason: "disabled" | "tier_restriction" | "already_run" | "activation_criteria_failed";
  details?: string;
  timestamp: number;
}

export interface DecisionCompleteEvent {
  decision: any;
  timestamp: number;
}

export interface ErrorEvent {
  error: string;
  limit?: number;
  currentCount?: number;
  upgradeRequired?: boolean;
  timestamp: number;
}

export interface InsufficientCreditsEvent {
  cortex: string;
  requiredCents: number;
  currentBalanceCents: number;
  shortfallCents: number;
  timestamp: number;
}

export interface CompleteEvent {
  message: string;
  timestamp: number;
}

export type StreamEvent = 
  | { type: 'connected'; data: { message: string; timestamp: number } }
  | { type: 'conversational'; data: ConversationalEvent }
  | { type: 'decision_cycle_started'; data: { message: string; timestamp: number } }
  | { type: 'cortex_started'; data: CortexStartedEvent }
  | { type: 'cortex_completed'; data: CortexCompletedEvent }
  | { type: 'cortex_waiting'; data: CortexWaitingEvent }
  | { type: 'cortex_blocked'; data: CortexBlockedEvent }
  | { type: 'cortex_skipped'; data: CortexSkippedEvent }
  | { type: 'insufficient_credits'; data: InsufficientCreditsEvent }
  | { type: 'cortex_summary'; data: { activated: string[]; completed: string[]; total: number; timestamp: number } }
  | { type: 'decision_complete'; data: DecisionCompleteEvent }
  | { type: 'error'; data: ErrorEvent }
  | { type: 'complete'; data: CompleteEvent };

export interface StreamCallbacks {
  onConnected?: (event: { message: string; timestamp: number }) => void;
  onConversational?: (event: ConversationalEvent) => void;
  onCortexStarted?: (event: CortexStartedEvent) => void;
  onCortexCompleted?: (event: CortexCompletedEvent) => void;
  onCortexWaiting?: (event: CortexWaitingEvent) => void;
  onCortexBlocked?: (event: CortexBlockedEvent) => void;
  onCortexSkipped?: (event: CortexSkippedEvent) => void;
  onInsufficientCredits?: (event: InsufficientCreditsEvent) => void;
  onDecisionComplete?: (event: DecisionCompleteEvent) => void;
  onError?: (event: ErrorEvent) => void;
  onComplete?: (event: CompleteEvent) => void;
}

/**
 * Stream a cortex request to the Cortex API
 * 
 * Handles all interaction types: conversations, decisions, file analysis, questions, etc.
 * 
 * Returns an AbortController that can be used to cancel the stream.
 */
export async function streamCortex(
  request: CortexRequest,
  callbacks: StreamCallbacks
): Promise<AbortController> {
  // Use getValidToken to auto-refresh if needed
  const { getValidToken } = await import('../auth/token-storage.js');
  const token = await getValidToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const abortController = new AbortController();

  try {
    const response = await fetch(`${CORTEX_API_BASE_URL}/api/decisions/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: abortController.signal,
    });

    if (!response.ok) {
      // Handle HTTP error (will show toast notification)
      const errorMessage = await handleHTTPError(response, 'Cortex API');
      
      // Try to get more details from response
      let errorData: { error?: string } = { error: errorMessage };
      try {
        const clonedResponse = response.clone();
        errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
      } catch {
        errorData = { error: errorMessage };
      }
      
      const errorEvent: ErrorEvent = {
        error: errorData.error || errorMessage || `HTTP ${response.status}`,
        timestamp: Date.now(),
      };
      
      if (callbacks.onError) {
        callbacks.onError(errorEvent);
      }
      
      return abortController;
    }

    // Check if response is SSE
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('text/event-stream')) {
      log.error('[CortexAPI] Expected SSE stream but got:', { contentType });
      if (callbacks.onError) {
        callbacks.onError({
          error: 'Invalid response format: expected SSE stream',
          timestamp: Date.now(),
        });
      }
      return abortController;
    }

    // Parse SSE stream using eventsource-parser library
    const reader = response.body?.getReader();
    if (!reader) {
      log.error('[CortexAPI] No response body reader available');
      if (callbacks.onError) {
        callbacks.onError({
          error: 'No response body available',
          timestamp: Date.now(),
        });
      }
      return abortController;
    }

    const decoder = new TextDecoder();
    
    // Create SSE parser with proper error handling
    const parser = createParser({
      onRetry: (interval: number) => {
        log.debug('[CortexAPI] SSE reconnect interval:', { interval });
      },
      onEvent: (event: EventSourceMessage) => {
        const eventType = event.event || 'message';
        const eventData = event.data;

        if (!eventData) {
          log.debug(`[CortexAPI] Empty data for event: ${eventType}`);
          return;
        }

        try {
          const data = JSON.parse(eventData);
          
          // Route to appropriate callback based on event type
          switch (eventType) {
            case 'connected':
              log.debug('[CortexAPI] Stream connected');
              if (callbacks.onConnected) {
                callbacks.onConnected(data as { message: string; timestamp: number });
              }
              break;
              
            case 'conversational':
              if (callbacks.onConversational) {
                callbacks.onConversational(data as ConversationalEvent);
              }
              break;
              
            case 'decision_cycle_started':
              log.debug('[CortexAPI] Decision cycle started');
              break;
              
            case 'cortex_started':
              if (callbacks.onCortexStarted) {
                callbacks.onCortexStarted(data as CortexStartedEvent);
              }
              break;
              
            case 'cortex_completed':
              if (callbacks.onCortexCompleted) {
                callbacks.onCortexCompleted(data as CortexCompletedEvent);
              }
              break;
              
            case 'cortex_waiting':
              if (callbacks.onCortexWaiting) {
                callbacks.onCortexWaiting(data as CortexWaitingEvent);
              }
              break;
              
            case 'cortex_blocked':
              if (callbacks.onCortexBlocked) {
                callbacks.onCortexBlocked(data as CortexBlockedEvent);
              }
              break;
              
            case 'cortex_skipped':
              if (callbacks.onCortexSkipped) {
                callbacks.onCortexSkipped(data as CortexSkippedEvent);
              }
              break;
              
            case 'insufficient_credits':
              if (callbacks.onInsufficientCredits) {
                callbacks.onInsufficientCredits(data as InsufficientCreditsEvent);
              }
              break;
              
            case 'cortex_summary':
              log.debug('[CortexAPI] Cortex summary:', data);
              break;
              
            case 'decision_complete':
              if (callbacks.onDecisionComplete) {
                callbacks.onDecisionComplete(data as DecisionCompleteEvent);
              }
              break;
              
            case 'error':
              if (callbacks.onError) {
                callbacks.onError(data as ErrorEvent);
              }
              break;
              
            case 'complete':
              if (callbacks.onComplete) {
                callbacks.onComplete(data as CompleteEvent);
              }
              break;
              
            default:
              log.debug(`[CortexAPI] Unknown event type: ${eventType}`, data);
          }
        } catch (error) {
          log.error('[CortexAPI] Failed to parse SSE event data:', {
            error: error instanceof Error ? error.message : String(error),
            eventType,
            rawData: eventData?.substring(0, 200), // Log first 200 chars for debugging
          });
          
          // Emit error callback if JSON parsing fails
          if (callbacks.onError) {
            callbacks.onError({
              error: `Failed to parse event data: ${error instanceof Error ? error.message : String(error)}`,
              timestamp: Date.now(),
            });
          }
        }
      }
    });

    // Process SSE stream chunks
    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // Feed decoded chunk to parser
        const chunk = decoder.decode(value, { stream: true });
        parser.feed(chunk);
      }
    } catch (parseError) {
      log.error('[CortexAPI] SSE stream parsing error:', { error: parseError });
      if (callbacks.onError) {
        callbacks.onError({
          error: `Stream parsing error: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
          timestamp: Date.now(),
        });
      }
    }
  } catch (error) {
    // Handle fetch error (will show toast notification)
    if (error instanceof Error && error.name === 'AbortError') {
      log.debug('[CortexAPI] Stream aborted');
      return abortController;
    }
    
    handleFetchError(error, 'Cortex API');
    
    if (callbacks.onError) {
      callbacks.onError({
        error: error instanceof Error ? error.message : 'Network error',
        timestamp: Date.now(),
      });
    }
  }

  return abortController;
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{ status: string; service: string; timestamp: string } | null> {
  try {
    const response = await fetch(`${CORTEX_API_BASE_URL}/health`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    log.error('[CortexAPI] Health check failed:', { error });
    return null;
  }
}

